import React, {
  useState, useContext, useRef, useEffect, useCallback,
} from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import Truncate from 'react-truncate';
import CircularProgress from '@material-ui/core/CircularProgress';
import { styled as muiStyled } from '@material-ui/core/styles';
import styled from 'styled-components';
import { useMutation } from '@apollo/react-hooks';
import Snackbar from '@material-ui/core/Snackbar';
import MuiAlert from '@material-ui/lab/Alert';

import CircularLoadingIndicator from 'components/indicators/CircularLoadingIndicator';
import uploadFileMutation from 'network/uploadFile';
import { APP, EVENT_TOKEN, BRAND_COLOR } from 'appenv';
import { useSelector } from 'react-redux';
import SendBtnIcon from 'assets/sendBtn.svg';
import SendBtnGrayIcon from 'assets/sendBtnGray.svg';
import ErrorIcon from 'assets/errorIcon.svg';
import ErrorHollowIcon from 'assets/errorHollowIcon.svg';
import FileActiveIcon from 'assets/fileActiveIcon.svg';
import PDFIcon from 'assets/pdfIcon.svg';
import FileCancelBtn from 'assets/fileCancelBtn.svg';
import { selectMessagesByTargetId } from 'models/messages';
import UserAvatar from 'components/UserAvatar';
import pushChatRoomNotification from 'network/pushChatRoomNotification';
import { UserSessionContext } from '../UserSession';

const Container = styled.div`
  position: relative;
  width: 100%;
  height: calc(100% - 44px);
  background-color: #F0F1F2;
  -webkit-overflow-scrolling: touch;
`;

const ErrorMsgContainer = styled.div`
  background-color: #EDD5D1;
  display: flex;
  height: 40px;
  padding: 11px 16px;
  color: #484848;
  font-size: 14px;

  img {
    width: 18px;
    height: 18px;
    margin-right: 16px;
  }
`;

const ChatBody = styled.div`
  box-sizing: border-box;
  padding: 0 20px;
  margin: 10px 0 0;
  height: ${({ errorMsg, hasFile }) => (
    (errorMsg && hasFile && 'calc(100% - 202px)')
    || (!errorMsg && !hasFile && 'calc(100% - 66px)')
    || (errorMsg && !hasFile && 'calc(100% - 106px)')
    || (!errorMsg && hasFile && 'calc(100% - 162px)'))};
  overflow-y: scroll;
  -webkit-overflow-scrolling: touch;
`;

const StyledDate = styled.div`
  text-align: center;

  span {
    color: #FFF;
    border-radius: 2px;
    font-size: 12px;
    padding: 4px;
    background-color: #C9C9C9;
  }
`;

const StyledInput = styled.input`
  background: rgba(255, 255, 255, 0.5);
  outline: 0;
  border: 1px solid #DBDEE2;
  padding: 0 5px;
  border-radius: 15px;
  margin: 10px 16px;
  height: 33px;
  width: calc(100% - 50px);
  box-sizing: border-box;
  font-size: 16px;
`;

const SendBtn = styled.img`
  width: 24px;
  height: 24px;
  margin-right: 20px;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
`;

const FooterContainer = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  background-color: #F8F8F8;
`;

const MessageContainer = styled.div`
  text-decoration: none;
  color: #3498DB;
  clear: both;
  display: flex;
  margin: 24px 0;

  &.self {
    text-align: right;
    flex-direction: row-reverse;

    .text-column .msg-group {
      justify-content: flex-end;
    }
  }

  .text-column {
    margin: 0 10px;
    display: block;
    max-width: 50%;
    word-break: break-word;

    .msg-group {
      display: flex;
      justify-content: flex-start;

      img.error-icon {
        margin: 0 10px;
        cursor: pointer;
      }
    }

    .send-time {
      color: #A2AAB3;
      font-size: 13px;
    }
  }
`;

const Message = styled.div`
  display: inline-flex;
  position: relative;
  padding: ${({ isAutoSent }) => (isAutoSent ? 0 : '16px')};
  font-size: 14px;
  border-radius: 8px;
  margin: 10px 0;
  width: fit-content;
  text-align: left;
  background-color: ${({ isSelfSent, isAutoSent }) => (isAutoSent && 'unset')
  || (isSelfSent && '#5C8DF3')
  || (!isSelfSent && '#FFF')};
  color: ${({ isSelfSent, isAutoSent }) => (isAutoSent && '#999')
  || (isSelfSent && '#fff')
  || (!isSelfSent && '#484848')};
`;

const UserInfo = styled.div`
  font-size: 14px;
  font-weight: bold;
  color: #484848;
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
`;

const UserTag = styled.span`
  border-radius: 2px;
  background-color: #FFB7B5;
  color: #FFF;
  font-size: 12px;
  padding: 0 5px;
  margin-left: 9px;
`;

const UserDescription = styled.div`
  color: #484848;
  font-size: 12px;
  margin-top: 8px;
`;

const LoadingIndicatorContainer = styled.div`
  width: 100%;
  height: 100%;
  padding: 50px;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #F0F1F2;
`;

const MoreMessagesBtn = styled.div`
  text-align: center;
  letter-spacing: -0.408px;
  color: #5C8DF3;
  cursor: pointer;
  margin-bottom: 16px;
`;

const StyledFileIcon = styled.div`
  cursor: pointer;

  img {
    height: 24px;
    width: 24px;
    margin-left: 16px;
  }
`;

const StyledFilePreview = styled.div`
  background-color: #FFF;
  width: 20%;
  height: 64px;
  border-radius: 4px;
  border: 1px solid #DBDEE2;
  margin: 16px 24px;
  display: flex;
  align-items: center;
  position: fixed;

  .file-info {
    width: 70%;
    margin-left: 10px;

    .file-name {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      color: #17181E;
      font-size: 14px;
    }

    .file-size {
      color: #999;
      font-size: 12px;
    }
  }

  img.file-type-icon {
    width: 20px;
    height: 20px;
    margin-left: 16px;
  }
`;

const StyledFilePreviewInChat = styled.a`
  text-decoration: none;
  background-color: #FFF;
  height: 84px;
  border-radius: 4px;
  border: 1px solid #DBDEE2;
  padding: 15px;
  display: flex;
  align-items: center;
  min-width: 280px;
  margin: 10px 0;

  img.file-type-icon {
    margin: 0;
  }

  .file-info {
    width: 80%;
    margin-left: 10px;
    text-align: left;

    .file-name {
      color: #17181E;
      font-size: 14px;
    }

    .file-size {
      color: #999;
      font-size: 12px;
    }
  }
`;

const StyledCancelBtn = styled.img`
  position: absolute;
  right: 6px;
  top: 6px;
  cursor: pointer;
`;

const FilePreviewErrorMsg = styled.div`
  color: #E2654D;
  font-size: 14px;
`;

const SnackbarContainer = styled.div`
  position: absolute;
  z-index: 1;
  width: 100%;

  & > div {
    &.MuiSnackbar-root {
      position: unset;
    }

    transform: none;
  }
`;

const StyledCircularProgress = muiStyled(CircularProgress)({
  color: BRAND_COLOR || '#1976D2',
});

const KB = 1000;
const MB = 1000000;
const GB = 1000000000;

const ChatWindow = ({
  joiningChat,
  status,
  sendMessage,
  resendMessage,
  getHistoryMsg,
  hasMoreMsg,
  handleAvatarOnclick,
}) => {
  const { userSession } = useContext(UserSessionContext);
  const [messageValue, setMessageValue] = useState('');
  const [file, setFile] = useState({});
  const [errorMsg, setErrorMsg] = useState('');
  const messageRef = useRef();
  const textInputRef = useRef();
  const fileInputRef = useRef();
  const EnterBtnCode = 13;
  const { targetId, type } = useSelector((state) => state.chatTarget);
  const messages = useSelector(selectMessagesByTargetId(targetId));
  const [autoscroll, setAutoscroll] = useState(true);
  const [firstMsgId, setFirstMsgId] = useState(null);
  const [alertOpen, setAlertOpen] = useState(false);

  const [upload] = useMutation(uploadFileMutation);

  const handleMessageSend = useCallback(async () => {
    if (messageValue) {
      const message = messageValue;
      setMessageValue('');
      if (type === 'system') {
        const success = await pushChatRoomNotification(userSession.authToken, message);
        setAlertOpen(success);
        window.trackingEvent('ChatSendSystemMessage', userSession.email, targetId);
      } else {
        sendMessage(message);
        if (type === 'private') {
          window.trackingEvent('ChatSendPrivateMessage', userSession.email, targetId);
        } else {
          window.trackingEvent('ChatSendMessage', userSession.email, targetId);
        }
      }
      textInputRef.current.focus();
    }
    if (file.size && type !== 'system') {
      file.isUploading = true;
      file.hasError = false;
      upload({
        variables: {
          eventToken: EVENT_TOKEN,
          file,
          uniqueToken: userSession.authToken,
        },
      }).then((res) => {
        const uploadedFile = {
          name: file.name,
          size: file.size,
          type: file.type,
          fileUrl: res.data.uploadFile.attachment.fileUrl,
        };
        sendMessage(uploadedFile, true);
        if (type === 'private') {
          window.trackingEvent('ChatPrivateUploadFile', userSession.email, targetId);
        } else {
          window.trackingEvent('ChatUploadFile', userSession.email, targetId);
        }
        fileInputRef.current.value = null;
        setFile({});
      }).catch((error) => {
        const fileClone = new File([file], file.name, { type: file.type });
        fileClone.hasError = true;
        fileClone.isUploading = false;
        setFile(fileClone);
        console.log(error);
      }, [file]);
    }
  }, [messageValue, sendMessage, file]);

  const renderFileSize = (fileSize) => {
    if (fileSize < 1 * KB) {
      return `${fileSize}B`;
    }
    if (fileSize < 1 * MB) {
      return `${(fileSize / KB).toFixed(1)}kB`;
    }
    if (fileSize < 1 * GB) {
      return `${(fileSize / MB).toFixed(1)}MB`;
    }
    console.warn('TOO LARGE!!!');
    return '';
  };

  useEffect(() => {
    if (joiningChat) return;
    if (status === 'offline') {
      setErrorMsg('Network unavailable, check network');
    } else if (status === 'disconnected') {
      setErrorMsg('Cannot connect to the chatroom');
    } else {
      setErrorMsg('');
    }
  }, [status]);

  useEffect(() => {
    if (autoscroll && messageRef.current) {
      messageRef.current.scrollTop = messageRef.current.scrollHeight;
    } else if (firstMsgId) {
      const targetComponent = document.getElementById(firstMsgId);
      messageRef.current.scrollTop = targetComponent.offsetTop - targetComponent.clientHeight;
      setFirstMsgId(null);
    }
  }, [messages]);

  const onScroll = (e) => {
    setAutoscroll(e.target.scrollHeight <= e.target.scrollTop + e.target.clientHeight);
  };

  const fetchMoreMessages = () => {
    setFirstMsgId((messages[0] || {}).id);
    getHistoryMsg(null);
  };

  const onKeydown = useCallback((event) => {
    switch (event.keyCode) {
      case EnterBtnCode:
        handleMessageSend();
        break;
      default:
    }
  }, [handleMessageSend]);

  const displaySentTime = (timestamp) => {
    const date = new Date(timestamp);
    const hours = date.getHours().toString();
    const minutes = date.getMinutes().toString();
    const formatH = hours.length === 1 ? `0${hours}` : hours;
    const formatM = minutes.length === 1 ? `0${minutes}` : minutes;
    return `${formatH}:${formatM}`;
  };

  const displayDateTag = (date) => {
    const today = moment();
    if (today.isSame(date, 'day')) {
      return 'Today';
    }
    return date.format('ddd, MMM D');
  };

  const renderFileUpload = () => {
    if (type === 'system') return null;
    switch (APP) {
      case 'hkstp608': return null;
      default: {
        return (
          <StyledFileIcon onClick={() => fileInputRef.current.click()}>
            <img src={FileActiveIcon} alt="upload file" />
          </StyledFileIcon>
        );
      }
    }
  };

  if (joiningChat) {
    return (
      <LoadingIndicatorContainer>
        <CircularLoadingIndicator />
      </LoadingIndicatorContainer>
    );
  }

  let dateFlag;

  const canSendMessage = () => {
    const customAttendeeFields = userSession.customAttendeeFields || {};
    return type !== 'system' || Boolean(customAttendeeFields.vexpo_organizer);
  };

  return (
    <Container>
      {
        errorMsg && (
          <ErrorMsgContainer>
            <img src={ErrorIcon} alt="error" />
            {errorMsg}
          </ErrorMsgContainer>
        )
      }
      <SnackbarContainer>
        <Snackbar open={alertOpen} autoHideDuration={3000} onClose={() => setAlertOpen(false)}>
          <MuiAlert elevation={6} variant="filled" onClose={() => setAlertOpen(false)} severity="success">
            All messages will be delivered within minutes
          </MuiAlert>
        </Snackbar>
      </SnackbarContainer>
      <ChatBody ref={messageRef} errorMsg={errorMsg} hasFile={file.size} onScroll={onScroll}>
        { hasMoreMsg && <MoreMessagesBtn onClick={fetchMoreMessages}>Load Earlier Messages</MoreMessagesBtn>}
        {
          messages.map((msg) => {
            const msgDate = moment(msg.sentTime);
            const isRenderDate = msgDate.format('DD/MM/YYYY') !== dateFlag;
            const isSelfSent = msg.senderUserId === userSession.rongcloudUserId;
            dateFlag = msgDate.format('DD/MM/YYYY');
            return (
              <React.Fragment key={msg.id}>
                {(isRenderDate ? <StyledDate><span>{displayDateTag(msgDate)}</span></StyledDate> : null)}
                <MessageContainer className={isSelfSent ? 'self' : ''} id={msg.id}>
                  <UserAvatar name={msg?.content?.user?.name || 'user'} handleOnClick={() => handleAvatarOnclick(isSelfSent, msg.senderUserId, msg?.content?.user?.name)} />
                  <div className="text-column">
                    <UserInfo>
                      {msg?.content?.user?.name}
                      {
                        msg?.content?.user?.tag && (
                          <UserTag>{msg?.content?.user?.tag}</UserTag>
                        )
                      }
                    </UserInfo>
                    <UserDescription>
                      <Truncate lines={2} ellipsis="...">
                        {msg?.content?.user?.description}
                      </Truncate>
                    </UserDescription>
                    <div className="msg-group">
                      {(msg.sentFailed && <img role="presentation" src={ErrorHollowIcon} alt="Send failed" onClick={() => resendMessage(msg)} />)}
                      <Message
                        isAutoSent={msg?.content?.user?.autoSent || false}
                        isSelfSent={msg.senderUserId === userSession.rongcloudUserId}
                      >
                        {
                          msg.messageType === window.RongIMClient.MessageType.TextMessage && typeof (msg.content.content) === 'string' && (
                            window.RongIMLib.RongIMEmoji.symbolToEmoji(msg.content.content)
                          )
                        }
                        {
                          msg.messageType === window.RongIMClient.MessageType.FileMessage && (
                            <StyledFilePreviewInChat
                              href={msg.content.fileUrl}
                              target="_blank"
                              download
                              rel="noopener noreferrer"
                            >
                              <img className="file-type-icon" src={PDFIcon} alt="pdf" />
                              <div className="file-info">
                                <div className="file-name">
                                  <Truncate lines={2} ellipsis="...">
                                    {msg.content.name}
                                  </Truncate>
                                </div>
                                <div className="file-size">
                                  {renderFileSize(msg.content.size)}
                                </div>
                              </div>
                            </StyledFilePreviewInChat>
                          )
                        }
                      </Message>
                    </div>
                    <div className="send-time">
                      {displaySentTime(msg.sentTime)}
                    </div>
                  </div>
                </MessageContainer>
              </React.Fragment>
            );
          })
        }
      </ChatBody>
      {
        canSendMessage() && (
          <FooterContainer>
            <input
              ref={fileInputRef}
              onChange={(e) => setFile(e.target.files[0])}
              type="file"
              style={{ display: 'none' }}
              accept=".pdf"
            />
            {renderFileUpload()}
            <StyledInput ref={textInputRef} disabled={joiningChat} value={messageValue} onChange={(e) => setMessageValue(e.target.value)} onKeyDown={onKeydown} />
            <SendBtn disabled={!messageValue && !file} src={(messageValue || file) ? SendBtnIcon : SendBtnGrayIcon} onClick={handleMessageSend} alt="send" />
          </FooterContainer>
        )
      }
      {
        file.size && (
          <StyledFilePreview>
            <img className="file-type-icon" src={PDFIcon} alt="pdf" />
            <div className="file-info">
              <div className="file-name">
                <Truncate lines={1} ellipsis="...">
                  {file.name}
                </Truncate>
              </div>
              <div className="file-size">
                {renderFileSize(file.size)}
              </div>
              {
                file.isUploading && (
                  <StyledCircularProgress size={20} />
                )
              }
              {
                file.hasError && (
                  <FilePreviewErrorMsg>
                    Upload error
                  </FilePreviewErrorMsg>
                )
              }
            </div>
            <StyledCancelBtn onClick={() => setFile({})} src={FileCancelBtn} alt="cancel" />
          </StyledFilePreview>
        )
      }
    </Container>
  );
};

ChatWindow.propTypes = {
  joiningChat: PropTypes.bool.isRequired,
  status: PropTypes.string.isRequired,
  sendMessage: PropTypes.func.isRequired,
  resendMessage: PropTypes.func.isRequired,
  getHistoryMsg: PropTypes.func.isRequired,
  hasMoreMsg: PropTypes.bool.isRequired,
  handleAvatarOnclick: PropTypes.func.isRequired,
};

export default ChatWindow;

export {
  UserInfo,
  UserTag,
  UserDescription,
};
